import { CreditCardIcon, TruckIcon, ShoppingCartIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { Title } from "components/base/Title";
import { Accordion } from "components/base/Accordion";
import { useUser } from "context/user-context";
import { Button } from "components/base/Button";
import { useNavigate } from "react-router-dom";


export default function FAQ() {
  const { isAuthenticated } = useUser();
  const navigate = useNavigate();

  return (
    <>
      <section className="bg-primary-100 py-12 px-6 -mt-4">
        <Title type='subdisplay' centered className="mb-12">Comment ça marche ?</Title>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
          {[
            { icon: UserCircleIcon, title: "Je me connecte", text: "Accès réservé aux membres de la communauté." },
            { icon: ShoppingCartIcon, title: "Je choisis mes produits", text: "Ajoutez vos pots de miel au panier." },
            { icon: CreditCardIcon, title: "Je valide ma commande", text: "Le paiement est simple et sécurisé." },
            { icon: TruckIcon, title: "Je récupère mon miel", text: "Votre référent vous remet votre commande, en lot collectif." },
          ].map((step, i) => (
            <div key={i} className="text-center bg-white rounded-2xl shadow p-6 hover:-translate-y-1 transition-transform">
              <step.icon className="mx-auto text-primary-500 w-12 h-12 mb-4" />
              <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
              <p className="text-gray-600 text-sm">{step.text}</p>
            </div>
          ))}
        </div>
        {!isAuthenticated && (
          <div className="flex justify-center mt-12">
            <Button onClick={() => navigate("/connexion")}>Je me connecte !</Button>
          </div>
        )}
      </section>
      <section className="max-w-5xl mx-auto mt-12 px-6">
        <Title type='h3' bold className="mb-4">Questions fréquentes</Title>
        <div className="space-y-4">
          <FAQItem
            title="Qui peut commander sur la plateforme ?"
            content={
              <div className="leading-relaxed">
                <p>Le site est réservé aux membres de la communauté du producteur.</p>
                <p>Pour accéder à la plateforme, il faut vous connecter avec un compte et vous rattacher à un groupe.</p>
                <p>Cela permet de garantir que les livraisons restent dans un secteur connu du producteur, pour faciliter la distribution.</p>
              </div>
            } />

          <FAQItem
            title="Comment ça marche ?"
            content={
              <div className="leading-relaxed">
                <p>Une fois connecté, vous pouvez ajouter des produits à votre panier depuis la boutique.</p>
                <p>Quand vous validez et payez votre panier, votre commande est automatiquement rattachée à la prochaine livraison de votre groupe (voir <i>Quand les prochaines livraisons sont-elles prévues ?</i>).</p>
                <p>Le jour de la livraison, votre référent reçoit la commande groupée et vous remettra directement votre commande.</p>
              </div>
            } />

          <FAQItem
            title="Puis-je choisir mon lieu de livraison ?"
            content={
              <div className="leading-relaxed">
                <p>Le lieu de livraison est déterminé par votre profil via votre groupe.</p>
                <p>Il dépend du référent auquel vous êtes rattaché (par exemple votre service, votre entreprise ou votre groupe d'amis).</p>
                <p>Cela permet de simplifier la logistique pour le producteur et d’éviter les erreurs de distribution.</p>
              </div>
            } />

          <FAQItem
            title="Que se passe-t-il avant la livraison ?"
            content={
              <div className="leading-relaxed">
                <p>Quelques jours avant la livraison, les commandes sont bloquées afin que l’apiculteur puisse préparer les produits.</p>
                <p>Les commandes sont ensuite regroupés en lots (cartons ou étiquettes selon les besoins) afin de faciliter la distribution par le référent.</p>
              </div>
            } />

          <FAQItem
            title="Je suis référent, quel est mon rôle ?"
            content={
              <div className="leading-relaxed">
                <p>En tant que référent, vous êtes le point de contact principal pour votre lieu de livraison.</p>
                <p>Vous vous organisez directement avec l'apiculteur pour convenir de la date de livraison et du lieu de livraison.</p>
                <p>Dans votre espace personnel, vous disposez d'un suivi des livraisons qui vous permet de connaître à l'avance le contenu prévu pour votre groupe, afin d'anticiper la quantité de pots ou de cartons à réceptionner.</p>
                <p>Le jour venu, vous réceptionnez la commande groupée et la redistribuez aux utilisateurs rattachés à votre point relais.</p>
              </div>
            } />

          <FAQItem
            title="Quand les prochaines livraisons sont-elles prévues ?"
            content={
              <div className="leading-relaxed">
                <p>Le site affiche toujours la date et le lieu de la prochaine livraison, à la fois sur la page d'accueil et avant de passer commande.</p>
                <p>Si aucune livraison n'est encore prévue, vous pouvez tout de même effectuer un achat.</p>
                <p>Dès qu'une nouvelle livraison est programmée, votre panier sera automatiquement rattaché à cette livraison, et vous recevrez un email pour vous en informer.</p>
                <p>Vous pouvez également demander à votre référent de contacter le producteur pour organiser une livraison adaptée à votre groupe si nécessaire.</p>
              </div>
            } />
        </div>
      </section>
    </>
  );
}

export const FAQItem = ({ title, content }: { title: string; content: React.ReactNode }) => {
  return (
    <div className="border border-gray-200 rounded-lg">
      <Accordion title={title} bgColor="bg-gray-50" titleClassName="font-semibold">
        <div className="px-4 py-2">{content}</div>
      </Accordion>
    </div>
  );
}